/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { GlobalPositionStrategy, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';
import { MatMenu, MatMenuPanel } from '@angular/material/menu';
import { toPx } from '@gh/core-util';
import { Subscription } from 'rxjs';
import {
  CONTEXT_MENU_OPEN_DEFER_MS,
  CONTEXT_MENU_OUTSIDE_SUBSCRIBE_DEFER_MS,
} from './context-menu-overlay-timing';

/**
 * This code was mainly copy-pasted from @angular/material library (MdMenuTrigger directive)
 */
@Directive({
  standalone: false,
  selector: `[ghContextMenuTrigger]`,
})
export class ContextMenuTriggerDirective implements AfterViewInit, OnDestroy {
  /** References the menu instance that the trigger is associated with. */
  @Input('ghContextMenuTrigger') menu: MatMenuPanel;

  private _portal: TemplatePortal<any>;
  private overlayRef: Maybe<OverlayRef>;
  private _menuOpen: boolean = false;
  private _outsidePointerUnlisten?: () => void;
  private _onContextMenuBound?: (event: MouseEvent) => void;
  private _menuCloseSubscription?: Subscription;
  private _openDeferId?: ReturnType<typeof setTimeout>;
  private _outsideSubscribeDeferId?: ReturnType<typeof setTimeout>;
  private _destroyed = false;

  constructor(
    private overlay: Overlay,
    private element: ElementRef,
    private viewContainerRef: ViewContainerRef,
    private ngZone: NgZone,
  ) {
  }

  ngAfterViewInit(): void {
    this._menuCloseSubscription = this.menu.close.subscribe(() => this.closeMenu());
    const el = this.element.nativeElement;
    this._onContextMenuBound = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      this.openMenu(event);
    };
    el.addEventListener('contextmenu', this._onContextMenuBound, true);
  }

  ngOnDestroy(): void {
    this._destroyed = true;
    this._clearOpenTimers();
    this._menuCloseSubscription?.unsubscribe();
    this._menuCloseSubscription = void 0;
    const el = this.element.nativeElement;
    if (this._onContextMenuBound) {
      el.removeEventListener('contextmenu', this._onContextMenuBound, true);
    }
    this.destroyMenu();
  }

  /** Opens the menu. */
  openMenu(event: MouseEvent): void {
    if (this._menuOpen) {
      return;
    }
    this._clearOpenTimers();
    const clientX = event.clientX;
    const clientY = event.clientY;
    this._openDeferId = setTimeout(() => {
      this._openDeferId = void 0;
      if (this._destroyed || this._menuOpen) {
        return;
      }
      const positionEvent = { clientX, clientY } as MouseEvent;
      this._createOverlay(positionEvent).attach(this._portal);
      this._initMenu();
      this._outsideSubscribeDeferId = setTimeout(() => {
        this._outsideSubscribeDeferId = void 0;
        if (!this._destroyed) {
          this._subscribeOutsidePointerClose();
        }
      }, CONTEXT_MENU_OUTSIDE_SUBSCRIBE_DEFER_MS);
    }, CONTEXT_MENU_OPEN_DEFER_MS);
  }

  /** Closes the menu. */
  closeMenu(): void {
    this._clearOpenTimers();
    if (!this._menuOpen) {
      return;
    }
    this._setIsMenuOpen(false);
    this._unsubscribeOutsidePointerClose();
    if (this.overlayRef) {
      this._setMatMenuPanelOpen(false);
      this.overlayRef.detach();
    }
  }

  /** Removes the menu from the DOM. */
  destroyMenu(): void {
    this._clearOpenTimers();
    this._unsubscribeOutsidePointerClose();
    if (this.overlayRef) {
      this._setMatMenuPanelOpen(false);
      this.overlayRef.dispose();
      this.overlayRef = void 0;
    }
    this._setIsMenuOpen(false);
  }

  private _clearOpenTimers(): void {
    if (this._openDeferId !== void 0) {
      clearTimeout(this._openDeferId);
      this._openDeferId = void 0;
    }
    if (this._outsideSubscribeDeferId !== void 0) {
      clearTimeout(this._outsideSubscribeDeferId);
      this._outsideSubscribeDeferId = void 0;
    }
  }

  /** Focuses the menu trigger. */
  focus(): void {
    this.element.nativeElement.focus();
  }

  private _subscribeOutsidePointerClose(): void {
    this._unsubscribeOutsidePointerClose();
    if (!this.overlayRef) {
      return;
    }
    const pane = this.overlayRef.overlayElement;
    const onPointerDown = (ev: PointerEvent) => {
      if (ev.composedPath().includes(pane)) {
        return;
      }
      this.ngZone.run(() => this.menu.close.emit());
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    this._outsidePointerUnlisten = () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }

  private _unsubscribeOutsidePointerClose(): void {
    if (this._outsidePointerUnlisten) {
      this._outsidePointerUnlisten();
      this._outsidePointerUnlisten = void 0;
    }
  }

  /**
   * This method sets the menu state to open and focuses the first item if
   * the menu was opened via the keyboard.
   */
  private _initMenu(): void {
    this._setIsMenuOpen(true);
    this._setMatMenuPanelOpen(true);
    this.menu.focusFirstItem('mouse');
  }

  private _setMatMenuPanelOpen(isOpen: boolean): void {
    const panel = this.menu;
    if (panel instanceof MatMenu) {
      panel._setIsOpen(isOpen);
    }
  }

  // set state rather than toggle to support triggers sharing a menu
  private _setIsMenuOpen(isOpen: boolean): void {
    this._menuOpen = isOpen;
  }

  /**
   *  This method creates the overlay from the provided menu's template and saves its
   *  OverlayRef so that it can be attached to the DOM when openMenu is called.
   */
  private _createOverlay(event: MouseEvent): OverlayRef {
    if (!this.overlayRef) {
      this._portal = new TemplatePortal(this.menu.templateRef, this.viewContainerRef);
      const config = this._getOverlayConfig(event);
      this.overlayRef = this.overlay.create(config);
    } else {
      this.reposition(<GlobalPositionStrategy>this.overlayRef.getConfig().positionStrategy, event);
    }

    return this.overlayRef;
  }

  /**
   * This method builds the configuration object needed to create the overlay, the OverlayConfig.
   * @returns OverlayConfig
   */
  private _getOverlayConfig(event: MouseEvent): OverlayConfig {
    const overlayConfig = new OverlayConfig();
    overlayConfig.positionStrategy = this._getPosition(event);
    overlayConfig.hasBackdrop = false;
    overlayConfig.scrollStrategy = this.overlay.scrollStrategies.reposition();
    return overlayConfig;
  }

  private _getPosition(event: MouseEvent): GlobalPositionStrategy {
    return this.overlay.position()
      .global()
      .left(toPx(event.clientX))
      .top(toPx(event.clientY));
  }

  private reposition(positionStrategy: GlobalPositionStrategy, event: MouseEvent): void {
    positionStrategy
      .left(toPx(event.clientX))
      .top(toPx(event.clientY));
  }

}
